import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import { useGoBack } from "../../../hooks/useGoBack";
import ReasonModal from "../../dp/components/ReasonModal";
import { downloadBlob } from "../../dp/utils/format";
import { HOMEPAGE } from "../../home/routes";
import { OPERACOES_PAGE } from "../../operacoes/routes";
import {
  CAMPAIGN_STATUS_LABEL,
  closeQuizCampaignApi,
  exportQuizResultsApi,
  fmtDateTime,
  fmtScore,
  getQuizAttemptApi,
  getQuizCampaignApi,
  getQuizQuestionStatsApi,
  getQuizResultsApi,
  quizPublicUrl,
  resetQuizAttemptApi,
  toLocalInput,
  updateQuizCampaignApi,
  type QuizAttemptDetail,
  type QuizCampaign,
  type QuizParticipantStatus,
  type QuizQuestionStat,
  type QuizResultRow,
} from "../quizApi";
import { QUIZ_HUB_PAGE } from "../routes";
import { STATUS_PILL } from "./QuizHubPage";
import QuizReview from "./QuizReview";
import "./Quiz.css";

const PARTICIPANT_LABEL: Record<QuizParticipantStatus, string> = {
  NAO_INICIOU: "Não iniciou",
  EM_ANDAMENTO: "Em andamento",
  ENVIADO: "Enviado",
};

const PARTICIPANT_PILL: Record<QuizParticipantStatus, string> = {
  NAO_INICIOU: "ecoPill ecoPill--off",
  EM_ANDAMENTO: "ecoPill quizPill--warn",
  ENVIADO: "ecoPill",
};

const approvedLabel = (r: QuizResultRow) => (r.approved === null ? "—" : r.approved ? "Aprovado" : "Reprovado");

/** "Encerrada por Fulano (ADMIN) em 20/10/2026 10:48." — o autor vem do log de auditoria. */
const closedText = (c: QuizCampaign) => {
  if (c.closedBy) return `Encerrada por ${c.closedBy.label} em ${fmtDateTime(c.closedBy.at)}.`;
  if (c.closedAt) return `Encerrada em ${fmtDateTime(c.closedAt)} (antes do log de auditoria, sem registro de quem encerrou).`;
  return `Encerrada pelo prazo em ${fmtDateTime(c.closesAt)}.`;
};

const QuizCampaignPage: React.FC = () => {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const goBack = useGoBack(QUIZ_HUB_PAGE());

  const [campaign, setCampaign] = useState<QuizCampaign | null>(null);
  const [results, setResults] = useState<QuizResultRow[]>([]);
  const [stats, setStats] = useState<QuizQuestionStat[]>([]);
  const [busy, setBusy] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [resetTarget, setResetTarget] = useState<QuizResultRow | null>(null);
  const [attempt, setAttempt] = useState<QuizAttemptDetail | null>(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ closesAt: "", passingScore: "", showAnswers: false });

  const load = useCallback(async () => {
    try {
      const [c, r, s] = await Promise.all([getQuizCampaignApi(id), getQuizResultsApi(id), getQuizQuestionStatsApi(id)]);
      setCampaign(c);
      setResults(r);
      setStats(s);
    } catch (e: any) {
      toast.error(e?.message);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (!campaign) {
    return (
      <div className="quizPage">
        <main className="quizPage__main">
          <p className="quizPage__loading">Carregando...</p>
        </main>
      </div>
    );
  }

  const link = quizPublicUrl(campaign.slug);
  const forEmployees = campaign.audience === "ALL_EMPLOYEES";
  const approvedCount = results.filter((r) => r.approved).length;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copiado.");
    } catch {
      toast.error("Não foi possível copiar. Selecione o link e copie.");
    }
  };

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(
    `${campaign.title}: responda o quiz até ${fmtDateTime(campaign.closesAt)}. Entre com o seu CPF: ${link}`,
  )}`;

  const handleClose = async () => {
    try {
      setBusy(true);
      const closed = await closeQuizCampaignApi(id);
      toast.success("Campanha encerrada.");
      setConfirmClose(false);
      await load();
      // O log do encerramento é gravado depois da resposta: o autor vem do próprio encerramento.
      setCampaign((c) => (c && !c.closedBy ? { ...c, closedBy: closed.closedBy } : c));
    } catch (e: any) {
      toast.error(e?.message);
    } finally {
      setBusy(false);
    }
  };

  const handleExport = async () => {
    try {
      const blob = await exportQuizResultsApi(id);
      downloadBlob(blob, `quiz-${campaign.title}.csv`);
    } catch (e: any) {
      toast.error(e?.message);
    }
  };

  const openEdit = () => {
    setForm({
      closesAt: toLocalInput(campaign.closesAt),
      passingScore: campaign.passingScore === null ? "" : String(campaign.passingScore),
      showAnswers: campaign.showAnswers,
    });
    setEditing(true);
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    const score = form.passingScore.trim() === "" ? null : Number(form.passingScore);
    if (score !== null && (Number.isNaN(score) || score < 0 || score > 100)) return toast.error("A nota mínima vai de 0 a 100.");
    const closesAtIso = new Date(form.closesAt).toISOString();
    try {
      setBusy(true);
      await updateQuizCampaignApi(id, {
        ...(closesAtIso !== new Date(campaign.closesAt).toISOString() ? { closesAt: closesAtIso } : {}),
        passingScore: score,
        showAnswers: form.showAnswers,
      });
      toast.success("Campanha atualizada.");
      setEditing(false);
      await load();
    } catch (err: any) {
      toast.error(err?.message);
    } finally {
      setBusy(false);
    }
  };

  const openAttempt = async (r: QuizResultRow) => {
    if (!r.attemptId) return;
    try {
      setAttempt(await getQuizAttemptApi(id, r.attemptId));
    } catch (e: any) {
      toast.error(e?.message);
    }
  };

  const rowActions = (r: QuizResultRow) => (
    <div className="quizActions">
      {r.status === "ENVIADO" ? (
        <button type="button" className="ecoBtn ecoBtn--sm" onClick={() => openAttempt(r)}>
          Ver respostas
        </button>
      ) : null}
      {r.status !== "NAO_INICIOU" ? (
        <button type="button" className="ecoBtn ecoBtn--sm" onClick={() => setResetTarget(r)}>
          Liberar nova tentativa
        </button>
      ) : null}
    </div>
  );

  return (
    <div className="quizPage">
      <main className="quizPage__main">
        <EcoPageHeader
          crumbs={[
            { label: "Início", onClick: () => navigate(HOMEPAGE()) },
            { label: "Operações", onClick: () => navigate(OPERACOES_PAGE()) },
            { label: "Quiz", onClick: () => navigate(QUIZ_HUB_PAGE()) },
            { label: campaign.title },
          ]}
          title={campaign.title}
          subtitle={
            <>
              Quiz <strong>{campaign.quiz.title}</strong> ·{" "}
              <span className={STATUS_PILL[campaign.status]}>{CAMPAIGN_STATUS_LABEL[campaign.status]}</span>
            </>
          }
          onBack={goBack}
          backLabel="Voltar para Quiz"
          actions={
            <>
              <button type="button" className="ecoBtn" onClick={handleExport}>
                <EcoIcon name="download" size={18} />
                Exportar CSV
              </button>
              {campaign.status !== "ENCERRADA" ? (
                <button type="button" className="ecoBtn ecoBtn--danger" onClick={() => setConfirmClose(true)}>
                  Encerrar agora
                </button>
              ) : null}
            </>
          }
        />

        <section className="ecoCard quizPanel">
          <div className="quizPanel__headInline">
            <div>
              <h2 className="ecoCard__title">Link para os {forEmployees ? "colaboradores" : "supervisores"}</h2>
              <p className="ecoCard__subtitle">
                {campaign.status === "ENCERRADA"
                  ? `${closedText(campaign)} O link não aceita mais respostas.`
                  : campaign.status === "AGENDADA"
                    ? `Abre em ${fmtDateTime(campaign.opensAt)} e fica válido até ${fmtDateTime(campaign.closesAt)}.`
                    : `Válido até ${fmtDateTime(campaign.closesAt)}.`}
              </p>
            </div>
            <button type="button" className="ecoBtn ecoBtn--sm" onClick={openEdit}>
              <EcoIcon name="clock" size={16} />
              Prazo e resultado
            </button>
          </div>
          <div className="quizLink">
            <code className="quizLink__url">{link}</code>
            <div className="quizActions">
              <button type="button" className="ecoBtn ecoBtn--primary" onClick={copyLink}>
                <EcoIcon name="copy" size={18} />
                Copiar link
              </button>
              <a className="ecoBtn" href={whatsappHref} target="_blank" rel="noopener noreferrer">
                Enviar no WhatsApp
              </a>
            </div>
          </div>
        </section>

        <section className="quizKpis" aria-label="Resumo">
          <div className="ecoCard quizKpi">
            <span className="quizKpi__label">Responderam</span>
            <strong className="quizKpi__value">
              {campaign.submittedCount} de {campaign.participantCount}
            </strong>
          </div>
          <div className="ecoCard quizKpi">
            <span className="quizKpi__label">Nota média</span>
            <strong className="quizKpi__value">{fmtScore(campaign.averageScore)}</strong>
          </div>
          <div className="ecoCard quizKpi">
            <span className="quizKpi__label">Aprovados</span>
            <strong className="quizKpi__value">
              {campaign.passingScore === null ? "—" : `${approvedCount} (mín. ${campaign.passingScore}%)`}
            </strong>
          </div>
          <div className="ecoCard quizKpi">
            <span className="quizKpi__label">Gabarito no envio</span>
            <strong className="quizKpi__value">{campaign.showAnswers ? "Mostra" : "Só a nota"}</strong>
          </div>
        </section>

        <section className="ecoCard quizPanel quizPanel--flush" aria-labelledby="quiz-res">
          <div className="quizPanel__head">
            <div>
              <h2 id="quiz-res" className="ecoCard__title">{forEmployees ? "Colaboradores" : "Supervisores"}</h2>
              <p className="ecoCard__subtitle">Situação e nota de cada um do público da campanha.</p>
            </div>
          </div>
          {!results.length ? (
            <div className="quizPanel__pad">
              <p className="ecoEmpty">Ninguém no público.</p>
            </div>
          ) : (
            <>
              <div className="ecoTableWrap quizDesktop">
                <table className="ecoTable">
                  <thead>
                    <tr>
                      <th scope="col">{forEmployees ? "Colaborador" : "Supervisor"}</th>
                      <th scope="col">Situação</th>
                      <th scope="col" className="ecoTable__num">Nota</th>
                      <th scope="col">Resultado</th>
                      <th scope="col">Enviado em</th>
                      <th scope="col" className="ecoTable__num">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((r) => (
                      <tr key={r.user.id}>
                        <td className="quizTable__strong">
                          {r.user.nomeCompleto}
                          {r.resets.length ? (
                            <span className="quizTable__note">
                              {r.resets.length} tentativa{r.resets.length > 1 ? "s" : ""} liberada{r.resets.length > 1 ? "s" : ""}
                            </span>
                          ) : null}
                        </td>
                        <td>
                          <span className={PARTICIPANT_PILL[r.status]}>{PARTICIPANT_LABEL[r.status]}</span>
                        </td>
                        <td className="ecoTable__num">{fmtScore(r.score)}</td>
                        <td className="ecoTable__muted">{approvedLabel(r)}</td>
                        <td className="ecoTable__muted">{r.submittedAt ? fmtDateTime(r.submittedAt) : "—"}</td>
                        <td className="ecoTable__num">{rowActions(r)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ul className="quizCards quizMobile">
                {results.map((r) => (
                  <li key={r.user.id} className="quizCard quizCard--static">
                    <span className="quizCard__top">
                      <span className="quizCard__text">
                        <span className="quizCard__title">{r.user.nomeCompleto}</span>
                        <span className="quizCard__meta">
                          Nota {fmtScore(r.score)} · {approvedLabel(r)}
                        </span>
                      </span>
                      <span className={PARTICIPANT_PILL[r.status]}>{PARTICIPANT_LABEL[r.status]}</span>
                    </span>
                    {rowActions(r)}
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>

        <section className="ecoCard quizPanel" aria-labelledby="quiz-stats">
          <div>
            <h2 id="quiz-stats" className="ecoCard__title">Acerto por pergunta</h2>
            <p className="ecoCard__subtitle">Só as respostas enviadas que valem (sem as tentativas liberadas de novo).</p>
          </div>
          <ol className="quizStats">
            {stats.map((q) => (
              <li key={q.id} className="quizStats__item">
                <div className="quizStats__head">
                  <span className="quizStats__prompt">
                    {q.order}. {q.prompt}
                  </span>
                  <span className="quizBar">
                    <span className="quizBar__track" aria-hidden="true">
                      <span className="quizBar__fill" style={{ width: `${q.correctRate ?? 0}%` }} />
                    </span>
                    <span className="quizBar__label">
                      {q.correctRate === null ? "sem respostas" : `${fmtScore(q.correctRate)} de acerto`}
                    </span>
                  </span>
                </div>
                <ul className="quizStats__options">
                  {q.options.map((o) => (
                    <li key={o.id} className={o.isCorrect ? "is-correct" : undefined}>
                      <span>
                        {o.isCorrect ? <EcoIcon name="check" size={14} /> : null} {o.label}
                      </span>
                      <span className="quizCard__meta">{o.chosen}</span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        </section>
      </main>

      <EcoConfirmDialog
        open={confirmClose}
        title="Encerrar a campanha agora?"
        message="O link deixa de aceitar respostas na hora. Quem já começou e não enviou fica sem nota. Para reabrir, mude o prazo."
        confirmLabel="Encerrar"
        tone="danger"
        busy={busy}
        onConfirm={handleClose}
        onCancel={() => setConfirmClose(false)}
      />

      {resetTarget ? (
        <ReasonModal
          title="Liberar nova tentativa"
          description={
            <>
              A tentativa atual de <strong>{resetTarget.user.nomeCompleto}</strong> deixa de valer e ele pode responder de novo
              pelo mesmo link, enquanto a campanha estiver aberta. A nota anterior fica no histórico.
            </>
          }
          confirmLabel="Liberar"
          onClose={() => setResetTarget(null)}
          onConfirm={async (reason) => {
            await resetQuizAttemptApi(id, resetTarget.user.id, reason);
            toast.success("Nova tentativa liberada.");
            setResetTarget(null);
            await load();
          }}
        />
      ) : null}

      {attempt ? (
        <div className="ecoDialog__overlay" role="presentation">
          <div className="ecoDialog quizDialog--wide" role="dialog" aria-modal="true" aria-labelledby="quiz-attempt-title">
            <div className="quizDialog__head">
              <h2 id="quiz-attempt-title" className="ecoDialog__title">
                {attempt.user.nomeCompleto} · {fmtScore(attempt.score)}
              </h2>
              <button type="button" className="ecoBtn ecoBtn--sm ecoBtn--icon" data-modal-close onClick={() => setAttempt(null)} aria-label="Fechar">
                <EcoIcon name="x" size={16} />
              </button>
            </div>
            <QuizReview questions={attempt.questions} />
          </div>
        </div>
      ) : null}

      {editing ? (
        <div className="ecoDialog__overlay" role="presentation">
          <form className="ecoDialog" role="dialog" aria-modal="true" aria-labelledby="quiz-edit-title" onSubmit={handleSaveEdit}>
            <div className="quizDialog__head">
              <h2 id="quiz-edit-title" className="ecoDialog__title">
                Prazo e resultado
              </h2>
              <button type="button" className="ecoBtn ecoBtn--sm ecoBtn--icon" data-modal-close onClick={() => setEditing(false)} aria-label="Fechar">
                <EcoIcon name="x" size={16} />
              </button>
            </div>
            <div className="ecoField">
              <label htmlFor="e-closes">Válido até</label>
              <input
                id="e-closes"
                className="ecoInput"
                type="datetime-local"
                value={form.closesAt}
                onChange={(e) => setForm((f) => ({ ...f, closesAt: e.target.value }))}
                required
              />
            </div>
            <p className="quizHint">Mudar o prazo reabre a campanha encerrada.</p>
            <div className="ecoField">
              <label htmlFor="e-pass">
                Nota mínima (%) <span className="ecoLabel__optional">(vazio = sem aprovação)</span>
              </label>
              <input
                id="e-pass"
                className="ecoInput"
                type="number"
                min={0}
                max={100}
                value={form.passingScore}
                onChange={(e) => setForm((f) => ({ ...f, passingScore: e.target.value }))}
              />
            </div>
            <label className="quizCheck">
              <input type="checkbox" checked={form.showAnswers} onChange={(e) => setForm((f) => ({ ...f, showAnswers: e.target.checked }))} />
              <span>Mostrar o gabarito depois do envio</span>
            </label>
            <div className="ecoDialog__actions">
              <button type="button" className="ecoBtn" onClick={() => setEditing(false)} disabled={busy}>
                Cancelar
              </button>
              <button type="submit" className="ecoBtn ecoBtn--primary" disabled={busy}>
                Salvar
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
};

export default QuizCampaignPage;
